import React, { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Error,
  Refresh,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { fetchDashboardData, clearError } from '../../store/slices/dashboardSlice';


const EQUIPMENT_COLORS: Record<string, string> = {
  Operational: '#4caf50',
  Warning: '#ff9800',
  Critical: '#f44336',
  Maintenance: '#9c27b0',
};

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

interface AlertItem {
  id: number;
  message: string;
  severity: string;
  time: string;
}

const Dashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { kpiData, trendData, equipmentStatus, isLoading, error, lastUpdated } =
    useSelector((state: RootState) => state.dashboard);

  const [currentTime, setCurrentTime] = useState(new Date());
  const [recentAlerts, setRecentAlerts] = useState<AlertItem[]>([]);
  const [productionShifts, setProductionShifts] = useState<any[]>([]);

  // Keep live clock ticking
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch real data on mount
  useEffect(() => {
    dispatch(fetchDashboardData());
  }, [dispatch]);

  // Fetch alerts from API
  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    fetch(`${API_BASE}/analytics/alerts`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          const list = data.data ?? data;
          if (Array.isArray(list) && list.length > 0) {
            setRecentAlerts(list.slice(0, 5).map((a: any, i: number) => ({
              id: a.id ?? i,
              message: a.message ?? a.description ?? '',
              severity: a.severity ?? a.type ?? 'info',
              time: a.timestamp ? new Date(a.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
            })));
          }
        }
      })
      .catch(() => {/* alerts are non-critical, fail silently */});

    fetch(`${API_BASE}/analytics/production/shifts`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          const list = data.data ?? data;
          if (Array.isArray(list)) setProductionShifts(list);
        }
      })
      .catch(() => {/* shift data is non-critical, fail silently */});
  }, []);

  const handleRefresh = () => {
    dispatch(clearError());
    dispatch(fetchDashboardData());
  };

  // Build chart-friendly OEE data from Redux trend data
  const oeeChartData = trendData.map((t) => ({
    name: new Date(t.date).toLocaleDateString('en', { weekday: 'short' }),
    oee: t.oee,
    availability: t.availability,
    performance: t.performance,
    quality: t.quality,
  }));

  // Build pie-chart data from Redux equipment status
  const equipmentPieData = equipmentStatus
    ? [
        { name: 'Operational', value: equipmentStatus.operational, color: EQUIPMENT_COLORS.Operational },
        { name: 'Warning', value: equipmentStatus.warning, color: EQUIPMENT_COLORS.Warning },
        { name: 'Critical', value: equipmentStatus.critical, color: EQUIPMENT_COLORS.Critical },
        { name: 'Maintenance', value: equipmentStatus.maintenance, color: EQUIPMENT_COLORS.Maintenance },
      ]
    : [];

  // KPI values — use real data when available, otherwise show placeholders
  const oeeValue = kpiData ? `${kpiData.oee.toFixed(1)}%` : '—';
  const efficiencyValue = kpiData ? `${kpiData.efficiency.toFixed(1)}%` : '—';
  const qualityRateValue = kpiData ? `${kpiData.qualityRate.toFixed(1)}%` : '—';
  const activeAlertsValue = kpiData ? String(kpiData.activeAlerts) : '—';

  // ---- Sub-components ----

  const KPICard: React.FC<{
    title: string;
    value: string | number;
    trend: 'up' | 'down';
    trendValue: string;
    color: string;
  }> = ({ title, value, trend, trendValue, color }) => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div" color={color}>
              {isLoading ? <CircularProgress size={28} /> : value}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            {trend === 'up' ? (
              <TrendingUp style={{ color: '#4caf50' }} />
            ) : (
              <TrendingDown style={{ color: '#f44336' }} />
            )}
            <Typography variant="body2" style={{ marginLeft: 4 }}>
              {trendValue}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const AlertItemRow: React.FC<{ alert: AlertItem }> = ({ alert }) => {
    const getIcon = () => {
      switch (alert.severity) {
        case 'error':
          return <Error color="error" />;
        case 'warning':
          return <Warning color="warning" />;
        case 'success':
          return <CheckCircle color="success" />;
        default:
          return <CheckCircle color="info" />;
      }
    };

    return (
      <ListItem>
        <Box display="flex" alignItems="center" width="100%">
          {getIcon()}
          <Box ml={1} flexGrow={1}>
            <ListItemText primary={alert.message} secondary={alert.time} />
          </Box>
        </Box>
      </ListItem>
    );
  };

  // Fallback shift data when API returns nothing
  const shifts = productionShifts.length > 0 ? productionShifts : [];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Dashboard
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="textSecondary">
            {lastUpdated
              ? `Last updated: ${new Date(lastUpdated).toLocaleTimeString()}`
              : `Current time: ${currentTime.toLocaleTimeString()}`}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              isLoading ? <CircularProgress size={16} /> : <Refresh />
            }
            onClick={handleRefresh}
            disabled={isLoading}
          >
            {isLoading ? 'Refreshing…' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      {/* Error banner — does not hide the rest of the UI */}
      {error && (
        <Alert
          severity="warning"
          onClose={() => dispatch(clearError())}
          sx={{ mb: 2 }}
        >
          {error} — Showing last known or sample data.
        </Alert>
      )}

      {/* KPI Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Overall OEE"
            value={oeeValue}
            trend="up"
            trendValue="+2.1%"
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Production Efficiency"
            value={efficiencyValue}
            trend="up"
            trendValue="+1.8%"
            color="#4caf50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Quality Rate"
            value={qualityRateValue}
            trend="down"
            trendValue="-0.3%"
            color="#ff9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Active Alerts"
            value={activeAlertsValue}
            trend="down"
            trendValue="-3"
            color="#f44336"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* OEE Trend Chart */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              OEE Trends (Last 7 Days)
            </Typography>
            {isLoading && trendData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="80%">
                <CircularProgress />
              </Box>
            ) : oeeChartData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="80%">
                <Alert severity="info">No OEE trend data available from the analytics service.</Alert>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="90%">
                <LineChart data={oeeChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Line type="monotone" dataKey="oee" stroke="#1976d2" strokeWidth={3} name="OEE" />
                  <Line type="monotone" dataKey="availability" stroke="#4caf50" strokeWidth={2} name="Availability" />
                  <Line type="monotone" dataKey="performance" stroke="#ff9800" strokeWidth={2} name="Performance" />
                  <Line type="monotone" dataKey="quality" stroke="#9c27b0" strokeWidth={2} name="Quality" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Equipment Status Donut */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Equipment Status
            </Typography>
            {isLoading && !equipmentStatus ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="80%">
                <CircularProgress />
              </Box>
            ) : equipmentPieData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="80%">
                <Alert severity="info">No equipment status data available.</Alert>
              </Box>
            ) : (
              <>
                <ResponsiveContainer width="100%" height="70%">
                  <PieChart>
                    <Pie
                      data={equipmentPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {equipmentPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <Box mt={2}>
                  {equipmentPieData.map((item) => (
                    <Box key={item.name} display="flex" justifyContent="space-between" mb={1}>
                      <Box display="flex" alignItems="center">
                        <Box
                          width={12}
                          height={12}
                          bgcolor={item.color}
                          borderRadius="50%"
                          mr={1}
                        />
                        <Typography variant="body2">{item.name}</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="bold">
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Paper>
        </Grid>

        {/* Production Progress */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Today's Production Progress
            </Typography>
            {shifts.length > 0 ? (
              shifts.map((shift: any, idx: number) => {
                const completed = shift.completedQty ?? shift.produced ?? 0;
                const target = shift.targetQty ?? shift.planned ?? 1;
                const pct = Math.min(100, (completed / target) * 100);
                return (
                  <Box key={idx} mb={2}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">{shift.name ?? `Shift ${idx + 1}`}</Typography>
                      <Typography variant="body2">{completed} / {target} units</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4 }} />
                  </Box>
                );
              })
            ) : (
              <Typography variant="body2" color="textSecondary">
                No shift data available from the API.
              </Typography>
            )}
            {kpiData && (
              <Box mt={2} pt={1} borderTop="1px solid #eee">
                <Typography variant="body2" color="textSecondary">
                  Efficiency: <strong>{kpiData.efficiency.toFixed(1)}%</strong> &nbsp;|&nbsp;
                  OEE: <strong>{kpiData.oee.toFixed(1)}%</strong>
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Recent Alerts */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Alerts
            </Typography>
            {kpiData && kpiData.activeAlerts > 0 && (
              <Chip
                label={`${kpiData.activeAlerts} active alert${kpiData.activeAlerts !== 1 ? 's' : ''}`}
                color="error"
                size="small"
                sx={{ mb: 1 }}
              />
            )}
            {recentAlerts.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No recent alerts from API.
              </Typography>
            ) : (
              <List dense>
                {recentAlerts.map((alert) => (
                  <AlertItemRow key={alert.id} alert={alert} />
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
